import { Group, Modal, Stack, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import P1Regular from 'components/typography/P1Regular';
import RevisionList from 'components/RevisionList';
import { RevisionRow } from 'components/tables/data/revision/types';
import panic from 'errors/panic';
import { useEffect, useState } from 'react';
import Button from 'components/Button';

/**
 * Parameters of the BulkRejectConfirmMessage modal component.
 */
export interface BulkRejectConfirmMessageProps {
  title: string;
  message: string;
  revisions: RevisionRow[];
  opened: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void | Promise<void>;
}

interface FormValues {
  reason: string;
}

/**
 * Modal used to confirm bulk reject action with revision list and optional reason.
 */
export default function BulkRejectConfirmMessage({
  title,
  message,
  revisions,
  opened,
  onClose,
  onConfirm,
}: BulkRejectConfirmMessageProps) {
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    initialValues: {
      reason: '',
    },
  });

  /**
   * Submits the form.
   */
  async function submit({ reason }: FormValues) {
    try {
      setLoading(true);
      setClicked(true);
      await onConfirm(reason === '' ? undefined : reason);
      setLoading(false);
      form.reset();
      form.clearErrors();
      onClose();
    } catch (err: any) {
      panic(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setClicked(false);
    }
  }, [opened]);

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={title}
      zIndex={1000}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={24}>
          <P1Regular mb={4}>{message}</P1Regular>
          <RevisionList revisions={revisions} />
          <Textarea
            placeholder="Dôvod (nepovinné)"
            size="md"
            form="bulk-reject-confirm-message-modal"
            disabled={loading}
            name="objekto-reason"
            data-autofocus
            {...form.getInputProps('reason')}
          />
          {!loading && !clicked && (
            <Group position="apart">
              <Button
                variant="subtle-gray"
                onClick={() => {
                  form.reset();
                  form.clearErrors();
                  onClose();
                }}
              >
                Zrušiť
              </Button>
              <Button variant="primary" type="submit">
                Potvrdiť
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
